/**
 * HTML sanitization helpers — wrap DOMPurify so the rest of the app can
 * inject HTML into the DOM without exposing XSS sinks.
 *
 * Why this exists: the CMS lets admins type free-form HTML into rich-text
 * fields and the resulting strings end up in `dangerouslySetInnerHTML`
 * (e.g. CMS public pages, email-template previews, custom HTML blocks).
 * Even admin-authored content benefits from sanitization — defence in depth
 * against compromised admin sessions, copy-pasted markup containing
 * inline `<script>` tags, and `on*` event handlers from external editors.
 */
import DOMPurify from 'dompurify';

/**
 * Sanitize an HTML string for safe use inside `dangerouslySetInnerHTML`.
 * Allows the rich-text features the CMS needs (links, images, embeds via
 * data URIs, iframes for YouTube, etc.) but strips inline event handlers,
 * `<script>`, and javascript:/data: URIs that aren't whitelisted media.
 */
export function safeHtml(html) {
  if (!html || typeof html !== 'string') return '';
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['target', 'rel', 'allow', 'allowfullscreen', 'frameborder', 'loading'],
  });
}

/**
 * Convenience wrapper that returns the `{ __html }` object expected by
 * React's `dangerouslySetInnerHTML`. Pass the result straight through:
 *
 *   <div dangerouslySetInnerHTML={safeHtmlProp(content)} />
 */
export function safeHtmlProp(html) {
  return { __html: safeHtml(html) };
}

export default safeHtml;
